import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PricingHero from './components/PricingHero';
import PricingToggle from './components/PricingToggle';
import PricingTiers from './components/PricingTiers';
import FeatureComparisonTable from './components/FeatureComparisonTable';
import AnnualSavingsPanel from './components/AnnualSavingsPanel';
import PricingCTA from './components/PricingCTA';
import { PricingHeroSkeleton } from '../../components/SkeletonLoader';

const springEasing = [0.22, 1, 0.36, 1];

const navVariants = {
  initial: { opacity: 0, y: -20 },
  enter: { opacity: 1, y: 0, transition: { duration: 0.5, ease: springEasing } },
};

const mobileMenuVariants = {
  closed: { x: '100%', transition: { duration: 0.35, ease: springEasing } },
  open: { x: 0, transition: { duration: 0.4, ease: springEasing } },
};

const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAnnual, setIsAnnual] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const navLinks = [
    ['/landing-page', 'Hjem'],
    ['/about-us', 'Om oss'],
    ['/services-detail', 'Tjenester'],
    ['/pricing', 'Priser'],
    ['/contact', 'Kontakt'],
  ];

  return (
    <>
      <Helmet>
        <title>Priser — Veiassistent</title>
        <meta name="description" content="Se alle abonnementsplaner for Veiassistent. Velg mellom Gratis og Premium med full funksjonssammenligning og sparing ved årlig fakturering." />
      </Helmet>
      {/* Navigation */}
      <motion.nav
        variants={navVariants}
        initial="initial"
        animate="enter"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          height: '72px',
          background: 'rgba(26,29,32,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(224,228,232,0.06)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 48px',
        }}
      >
        <div style={{ maxWidth: '1440px', margin: '0 auto', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} className="pricing-page-nav-padding">
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }} onClick={() => navigate('/landing-page')}>
            <div style={{ width: '32px', height: '32px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5">
                <polygon points="3 11 22 2 13 21 11 13 3 11" />
              </svg>
            </div>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 700, color: 'var(--color-foreground)', letterSpacing: '0.04em' }}>Veiassistent</span>
          </div>

          {/* Nav links - desktop */}
          <div className="hidden-mobile" style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            {navLinks?.map(([href, label]) => {
              const isActive = location?.pathname === href;
              return (
                <button
                  key={href}
                  onClick={() => navigate(href)}
                  style={{
                    background: 'none',
                    border: 'none',
                    borderBottom: isActive ? '2px solid #FF5722' : '2px solid transparent',
                    padding: '2px 0',
                    fontFamily: 'var(--font-heading)',
                    fontSize: '13px',
                    fontWeight: isActive ? 700 : 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: isActive ? '#FF5722' : 'var(--color-muted-foreground)',
                    cursor: 'pointer',
                    transition: 'color 150ms ease, border-color 150ms ease',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = 'var(--color-foreground)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = 'var(--color-muted-foreground)'; }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Auth buttons - desktop */}
          <div className="hidden-mobile" style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate('/login')}
              style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '10px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}
            >
              Logg inn
            </button>
            <button
              onClick={() => navigate('/register')}
              style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '10px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}
            >
              Registrer deg
            </button>
          </div>

          {/* Hamburger - mobile */}
          <button
            className="show-mobile"
            onClick={() => setMobileOpen(p => !p)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '8px', zIndex: 110 }}
          >
            <div style={{ width: '24px', height: '20px', position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <motion.span animate={mobileOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }} transition={{ duration: 0.3, ease: springEasing }} style={{ display: 'block', height: '2px', width: '100%', background: mobileOpen ? '#FF5722' : 'var(--color-foreground)', borderRadius: '2px', transformOrigin: 'center' }} />
              <motion.span animate={mobileOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }} transition={{ duration: 0.2 }} style={{ display: 'block', height: '2px', width: '100%', background: 'var(--color-foreground)', borderRadius: '2px' }} />
              <motion.span animate={mobileOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }} transition={{ duration: 0.3, ease: springEasing }} style={{ display: 'block', height: '2px', width: '100%', background: mobileOpen ? '#FF5722' : 'var(--color-foreground)', borderRadius: '2px', transformOrigin: 'center' }} />
            </div>
          </button>
        </div>
      </motion.nav>
      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div key="overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 105, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }} />
            <motion.div key="panel" variants={mobileMenuVariants} initial="closed" animate="open" exit="closed" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '280px', zIndex: 106, background: 'rgba(26,29,32,0.98)', backdropFilter: 'blur(20px)', borderLeft: '1px solid rgba(224,228,232,0.08)', display: 'flex', flexDirection: 'column', paddingTop: '88px', paddingBottom: '32px', paddingLeft: '32px', paddingRight: '32px' }}>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '32px' }}>
                {navLinks?.map(([href, label], i) => {
                  const isActive = location?.pathname === href;
                  return (<motion.button key={href} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 + i * 0.06, duration: 0.35, ease: springEasing }} onClick={() => { setMobileOpen(false); navigate(href); }} style={{ background: isActive ? 'rgba(255,87,34,0.1)' : 'none', border: 'none', borderLeft: isActive ? '3px solid #FF5722' : '3px solid transparent', borderRadius: '0 4px 4px 0', padding: '14px 16px', fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: isActive ? 700 : 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: isActive ? '#FF5722' : 'var(--color-muted-foreground)', cursor: 'pointer', textAlign: 'left' }}>{label}</motion.button>);
                })}
              </nav>
              <div style={{ height: '1px', background: 'rgba(224,228,232,0.08)', marginBottom: '28px' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button onClick={() => { setMobileOpen(false); navigate('/login'); }} style={{ background: 'transparent', border: '1px solid rgba(224,228,232,0.2)', borderRadius: '2px', padding: '12px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-foreground)', cursor: 'pointer' }}>Logg inn</button>
                <button onClick={() => { setMobileOpen(false); navigate('/register'); }} style={{ background: '#FF5722', border: '2px solid #FF5722', borderRadius: '2px', padding: '12px 24px', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}>Registrer deg</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* Main content */}
      <main style={{ background: 'var(--color-background)', minHeight: '100vh' }}>
        {isLoading ? (
          <PricingHeroSkeleton />
        ) : (
          <>
            <PricingHero />

            {/* Pricing toggle + tiers section */}
            <section className="pricing-page-section-padding" style={{ padding: '80px 0 60px', background: 'var(--color-background)', position: 'relative' }}>
              <div style={{ position: 'absolute', top: 0, left: '48px', right: '48px', height: '1px', background: 'rgba(224,228,232,0.06)' }} />
              <div style={{ textAlign: 'center', marginBottom: '48px', padding: '0 24px' }}>
                <div style={{ display: 'inline-block', background: 'rgba(0,79,152,0.1)', border: '1px solid rgba(0,79,152,0.2)', borderRadius: '2px', padding: '4px 12px', marginBottom: '20px' }}>
                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#004F98' }}>Planer</span>
                </div>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: 'var(--color-foreground)', letterSpacing: '-0.01em', marginBottom: '12px' }}>Enkle, transparente priser</h2>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--color-muted-foreground)', maxWidth: '440px', margin: '0 auto 40px', lineHeight: 1.6 }}>Velg faktureringsperiode og finn planen som passer deg best.</p>
                <PricingToggle isAnnual={isAnnual} onToggle={() => setIsAnnual(prev => !prev)} />
              </div>
              <PricingTiers isAnnual={isAnnual} />
            </section>

            <AnnualSavingsPanel isAnnual={isAnnual} onToggle={() => setIsAnnual(prev => !prev)} />
            <FeatureComparisonTable />
            <PricingCTA />
          </>
        )}

        {/* Footer */}
        <footer className="pricing-page-footer-padding" style={{ background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(224,228,232,0.06)', padding: '40px 48px', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '16px', cursor: 'pointer' }} onClick={() => navigate('/landing-page')}>
            <div style={{ width: '24px', height: '24px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
            </div>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '15px', fontWeight: 700, color: 'var(--color-foreground)', letterSpacing: '0.04em' }}>Veiassistent</span>
          </div>
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)', letterSpacing: '0.04em' }}>
            © {new Date()?.getFullYear()} Veiassistent AS. Alle rettigheter forbeholdt.
          </p>
        </footer>
      </main>
    </>
  );
};

export default PricingPage;
